import type { ProcessCellForExportParams } from "ag-grid-enterprise";
import { isPercentScaledText, readPercentLabel } from "./valueParsers";

/**
 * Copies a percent cell as the number its label shows, a numeric cell's value,
 * and every other cell's formatted text.
 *
 * AG Grid's default formats everything, so a cell showing "561,180" reached
 * the clipboard with separators the value parser then rejected. Bypassing the
 * formatter grid-wide would be worse: a date or status column would copy as
 * its raw backing value instead of what the reader sees.
 *
 * A percent cell is decided by its label, not its column type: the cube's
 * rows are heterogeneous metrics, so a percentage row sits in an `integer`
 * column whose raw fraction (0.59) would paste back as 0.59 %. "59.00 %"
 * copies as 59 — the unit the cell shows and its editor reads, and a plain
 * number for Excel.
 *
 * Clipboard only — CSV and Excel export keep their formatted output.
 */
export function copyNumbersUnformatted(params: ProcessCellForExportParams) {
  // Falls back to the raw value when the column has no formatter.
  const label = params.formatValue(params.value);
  if (isPercentScaledText(label)) {
    return readPercentLabel(label) ?? label;
  }
  const { type } = params.column.getColDef();
  const types = Array.isArray(type) ? type : type ? [type] : [];
  return types.some((t) => t === "integer" || t === "float")
    ? params.value
    : label;
}
